import React from 'react';
import { SignificationConfig } from '../../../../types/signification.types';
import './JournalEntryScreen.css';

interface JournalEntryScreenProps {
  config: SignificationConfig;
  entryNumber: number;
  totalEntries: number;
  /** Previous entry weights/positions for personal trend display. */
  priorEntries?: Array<{
    entryNumber: number;
    label: string; // e.g., "70% comfort"
  }>;
  onContinue: () => void;
}

/**
 * Journal mode entry screen — shows streak dots, day badge,
 * and optional personal trend before signification.
 */
export const JournalEntryScreen: React.FC<JournalEntryScreenProps> = ({
  config,
  entryNumber,
  totalEntries,
  priorEntries,
  onContinue,
}) => {
  const journalConfig = config.journalConfig;
  if (!journalConfig) return null;

  const frequencyLabel =
    journalConfig.frequency === 'daily' ? 'Daily' :
    journalConfig.frequency === 'weekly' ? 'Weekly' :
    journalConfig.frequency === 'biweekly' ? 'Biweekly' : 'Monthly';

  const today = new Date();
  const dayNum = today.getDate();
  const monthShort = today.toLocaleString('default', { month: 'short' });

  return (
    <div className="jrn-container">
      <div className="jrn-badge">
        JOURNAL &middot; {frequencyLabel.toUpperCase()} {entryNumber}
      </div>

      {/* Date badge + title */}
      <div className="jrn-date-row">
        <div className="jrn-date-badge">
          <span className="jrn-date-num">{dayNum}</span>
          <span className="jrn-date-month">{monthShort}</span>
        </div>
        <div className="jrn-date-title">
          {frequencyLabel} check-in
        </div>
      </div>

      {/* Streak dots */}
      <div className="jrn-streak">
        {Array.from({ length: totalEntries }, (_, i) => {
          const num = i + 1;
          const isDone = num < entryNumber;
          const isCurrent = num === entryNumber;
          const isFuture = num > entryNumber;

          return (
            <div
              key={num}
              className={`jrn-streak-dot ${isDone ? 'jrn-streak-dot--done' : ''} ${isCurrent ? 'jrn-streak-dot--current' : ''} ${isFuture ? 'jrn-streak-dot--future' : ''}`}
            >
              {isDone && (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              {isCurrent && (
                <div className="jrn-streak-pulse" />
              )}
            </div>
          );
        })}
      </div>

      {/* Personal trend (if available and enabled) */}
      {journalConfig.showPersonalTrend && priorEntries && priorEntries.length > 0 && (
        <div className="jrn-trend">
          <div className="jrn-trend-title">Your journey so far</div>
          {priorEntries.map(entry => (
            <div key={entry.entryNumber} className="jrn-trend-entry">
              <span className="jrn-trend-week">
                {frequencyLabel.charAt(0)}{entry.entryNumber}
              </span>
              <span className="jrn-trend-label">{entry.label}</span>
            </div>
          ))}
        </div>
      )}

      <div className="jrn-spacer" />

      <div className="jrn-actions">
        <button className="jrn-continue-btn" onClick={onContinue}>
          Start entry {entryNumber}
        </button>
      </div>
    </div>
  );
};
